/**
 * Scheme Service Types & Subtypes maintenance page.
 *
 * Two-panel layout:
 *   Left: Service Types list (filterable by scheme)
 *   Right: Subtypes for the selected type (with icon URL field)
 */
import React, { useState, useEffect } from 'react';
import { Layers, Plus, RefreshCw, Edit2, Trash2, ChevronRight, Workflow, Save } from 'lucide-react';
import { dealerService } from '../services/dealer.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';

// ─── Status Transitions Builder ───────────────────────────────────────────────
// Edits scheme_service_types.status_transitions ({ from: [to,...] }) using the
// status catalog as the option source (no hardcoded statuses).

function TransitionsPanel({ type, statuses, onSaved }) {
  const [map, setMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const init = {};
    Object.entries(type?.status_transitions || {}).forEach(([from, tos]) => {
      init[from] = new Set(Array.isArray(tos) ? tos : []);
    });
    setMap(init);
    setMsg('');
  }, [type?.id, type?.status_transitions]);

  const toggle = (from, to) => {
    setMap(prev => {
      const next = { ...prev };
      const set = new Set(next[from] || []);
      if (set.has(to)) set.delete(to); else set.add(to);
      next[from] = set;
      return next;
    });
  };

  const handleSave = async () => {
    const payload = {};
    Object.entries(map).forEach(([from, set]) => {
      if (set && set.size) payload[from] = Array.from(set);
    });
    setSaving(true);
    setMsg('');
    try {
      await dealerService.updateSchemeServiceType(type.id, { status_transitions: payload });
      setMsg('Transitions saved.');
      if (onSaved) onSaved(payload);
    } catch (err) {
      setMsg(err?.message || 'Failed to save transitions.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border bxshadowhd overflow-hidden mrgall">
      <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
        <span className="font-semibold text-gray-800 flex items-center gap-2">
          <Workflow className="w-4 h-4" /> Status Transitions — {type.service_type_name}
        </span>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50">
          <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save Transitions'}
        </button>
      </div>
      {statuses.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-400">No active statuses in catalog.</div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto divide-y divide-gray-100">
          {statuses.map(from => (
            <div key={from.code} className="px-4 py-3">
              <div className="text-sm font-medium text-gray-900 mb-2">
                {from.label} <span className="text-xs text-gray-400 font-mono">({from.code})</span>
                <span className="ml-2 text-xs text-gray-400">→ allowed next:</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {statuses.filter(to => to.code !== from.code).map(to => {
                  const on = map[from.code]?.has(to.code);
                  return (
                    <button key={to.code} type="button" onClick={() => toggle(from.code, to.code)}
                      className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                        on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}>
                      {to.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {msg && <div className="px-4 py-2 text-sm text-gray-700 border-t bg-gray-50">{msg}</div>}
    </div>
  );
}

// ─── Service Type Modal ───────────────────────────────────────────────────────

function TypeModal({ isOpen, onClose, onSave, type, schemes }) {
  const [form, setForm] = useState({ client_scheme_id: '', service_type_code: '', service_type_name: '', description: '', display_order: 0, is_active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(type
      ? { client_scheme_id: type.client_scheme_id, service_type_code: type.service_type_code, service_type_name: type.service_type_name, description: type.description || '', display_order: type.display_order || 0, is_active: type.is_active !== false }
      : { client_scheme_id: schemes[0]?.id || '', service_type_code: '', service_type_name: '', description: '', display_order: 0, is_active: true });
    setError('');
  }, [type, isOpen, schemes]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type: t, checked } = e.target;
    setForm(f => ({ ...f, [name]: t === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave({ ...form, client_scheme_id: parseInt(form.client_scheme_id), display_order: parseInt(form.display_order) || 0 });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-5 border-b">
          <h2 className="text-lg font-semibold">{type ? 'Edit Service Type' : 'New Service Type'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client Scheme *</label>
            <select name="client_scheme_id" value={form.client_scheme_id} onChange={handleChange} required
              disabled={!!type}
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
              {schemes.map(s => <option key={s.id} value={s.id}>{s.scheme_name || s.name || s.id}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type Code *</label>
              <input name="service_type_code" value={form.service_type_code} onChange={handleChange} required
                disabled={!!type}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input type="number" name="display_order" value={form.display_order} onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input name="service_type_name" value={form.service_type_name} onChange={handleChange} required
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={2}
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" name="is_active" id="type_active" checked={form.is_active} onChange={handleChange} />
            <label htmlFor="type_active" className="text-sm font-medium text-gray-700">Active</label>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Subtype Modal ────────────────────────────────────────────────────────────

function SubtypeModal({ isOpen, onClose, onSave, subtype, serviceTypeId }) {
  const [form, setForm] = useState({ subtype_code: '', subtype_name: '', description: '', icon: '', display_order: 0, is_active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(subtype
      ? { subtype_code: subtype.subtype_code, subtype_name: subtype.subtype_name, description: subtype.description || '', icon: subtype.icon || '', display_order: subtype.display_order || 0, is_active: subtype.is_active !== false }
      : { subtype_code: '', subtype_name: '', description: '', icon: '', display_order: 0, is_active: true });
    setError('');
  }, [subtype, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type: t, checked } = e.target;
    setForm(f => ({ ...f, [name]: t === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await onSave({ ...form, scheme_service_type_id: serviceTypeId, display_order: parseInt(form.display_order) || 0, icon: form.icon || null });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-5 border-b">
          <h2 className="text-lg font-semibold">{subtype ? 'Edit Subtype' : 'New Subtype'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtype Code *</label>
              <input name="subtype_code" value={form.subtype_code} onChange={handleChange} required
                disabled={!!subtype}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input type="number" name="display_order" value={form.display_order} onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input name="subtype_name" value={form.subtype_name} onChange={handleChange} required
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={2}
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Icon URL</label>
            <input name="icon" value={form.icon} onChange={handleChange}
              placeholder="https://cdn.example.com/icons/tow.png"
              className="w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            {form.icon && (
              <img src={form.icon} alt="preview" className="mt-2 h-10 object-contain rounded border" onError={e => e.target.style.display = 'none'} />
            )}
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" name="is_active" id="sub_active" checked={form.is_active} onChange={handleChange} />
            <label htmlFor="sub_active" className="text-sm font-medium text-gray-700">Active</label>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const SchemeServiceSetup = () => {
  const [schemes, setSchemes] = useState([]);
  const [filterScheme, setFilterScheme] = useState('');
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [typesLoading, setTypesLoading] = useState(true);
  const [subtypesLoading, setSubtypesLoading] = useState(false);

  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteTypeId, setDeleteTypeId] = useState(null);

  const [showSubtypeModal, setShowSubtypeModal] = useState(false);
  const [editingSubtype, setEditingSubtype] = useState(null);
  const [deleteSubtypeId, setDeleteSubtypeId] = useState(null);

  useEffect(() => {
    loadSchemes();
    loadStatuses();
  }, []);

  const loadStatuses = async () => {
    try {
      const data = await dealerService.getServiceStatuses({ is_active: true });
      setStatuses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading service statuses:', err);
    }
  };

  const handleTransitionsSaved = (payload) => {
    setSelectedType(prev => (prev ? { ...prev, status_transitions: payload } : prev));
    setTypes(prev => prev.map(t => (t.id === selectedType?.id ? { ...t, status_transitions: payload } : t)));
  };

  useEffect(() => {
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterScheme]);

  useEffect(() => {
    if (selectedType) fetchSubtypes(selectedType.id);
    else setSubtypes([]);
  }, [selectedType]);

  const loadSchemes = async () => {
    try {
      const data = await dealerService.getClientSchemes();
      setSchemes(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error('Error loading schemes:', err);
    }
  };

  const fetchTypes = async () => {
    setTypesLoading(true);
    try {
      const params = {};
      if (filterScheme) params.scheme_id = filterScheme;
      const data = await dealerService.getSchemeServiceTypes(params);
      setTypes(Array.isArray(data) ? data : data?.items || []);
      setSelectedType(null);
    } catch (err) {
      console.error('Error fetching types:', err);
    } finally {
      setTypesLoading(false);
    }
  };

  const fetchSubtypes = async (typeId) => {
    setSubtypesLoading(true);
    try {
      const data = await dealerService.getSchemeServiceSubtypes({ service_type_id: typeId });
      setSubtypes(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      console.error('Error fetching subtypes:', err);
    } finally {
      setSubtypesLoading(false);
    }
  };

  const handleSaveType = async (payload) => {
    if (editingType) {
      await dealerService.updateSchemeServiceType(editingType.id, payload);
    } else {
      await dealerService.createSchemeServiceType(payload);
    }
    fetchTypes();
  };

  const confirmDeleteType = async () => {
    try {
      await dealerService.deleteSchemeServiceType(deleteTypeId);
      setDeleteTypeId(null);
      if (selectedType?.id === deleteTypeId) setSelectedType(null);
      fetchTypes();
    } catch (err) {
      console.error('Error deleting type:', err);
    }
  };

  const handleSaveSubtype = async (payload) => {
    if (editingSubtype) {
      await dealerService.updateSchemeServiceSubtype(editingSubtype.id, payload);
    } else {
      await dealerService.createSchemeServiceSubtype(payload);
    }
    fetchSubtypes(selectedType.id);
  };

  const confirmDeleteSubtype = async () => {
    try {
      await dealerService.deleteSchemeServiceSubtype(deleteSubtypeId);
      setDeleteSubtypeId(null);
      fetchSubtypes(selectedType.id);
    } catch (err) {
      console.error('Error deleting subtype:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white flex justify-between items-center rounded-lg bxshadowhd mrgall py-3 padlr15 border">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <Layers className="w-6 h-6" /> Scheme Service Types & Subtypes
        </h1>
      </div>

      {/* Scheme filter */}
      <div className="mrgall flex gap-3 items-center">
        <label className="text-sm font-medium text-gray-700">Filter by Scheme:</label>
        <select value={filterScheme} onChange={e => setFilterScheme(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
          <option value="">All Schemes</option>
          {schemes.map(s => <option key={s.id} value={s.id}>{s.scheme_name || s.name || s.id}</option>)}
        </select>
      </div>

      <div className="mrgall grid grid-cols-2 gap-4">
        {/* ── Service Types ── */}
        <div className="bg-white rounded-lg border bxshadowhd overflow-hidden">
          <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
            <span className="font-semibold text-gray-800">Service Types</span>
            <div className="flex gap-1">
              <button onClick={fetchTypes} className="p-1 hover:bg-gray-200 rounded">
                <RefreshCw className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => { setEditingType(null); setShowTypeModal(true); }}
                className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          </div>
          {typesLoading ? <div className="p-4"><LoadingSpinner /></div> : (
            <ul className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
              {types.length === 0 ? (
                <li className="px-4 py-6 text-center text-sm text-gray-500">No types found</li>
              ) : types.map(t => (
                <li key={t.id}
                  onClick={() => setSelectedType(selectedType?.id === t.id ? null : t)}
                  className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-blue-50 ${selectedType?.id === t.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}>
                  <div>
                    <div className="font-medium text-sm text-gray-900">{t.service_type_name}</div>
                    <div className="text-xs text-gray-500 font-mono">{t.service_type_code}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {t.is_active ? 'Active' : 'Off'}
                    </span>
                    <button onClick={e => { e.stopPropagation(); setEditingType(t); setShowTypeModal(true); }}
                      className="p-1 text-blue-600 hover:text-blue-800">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={e => { e.stopPropagation(); setDeleteTypeId(t.id); }}
                      className="p-1 text-red-600 hover:text-red-800">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Subtypes ── */}
        <div className="bg-white rounded-lg border bxshadowhd overflow-hidden">
          <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50">
            <span className="font-semibold text-gray-800">
              Subtypes {selectedType ? `— ${selectedType.service_type_name}` : ''}
            </span>
            {selectedType && (
              <div className="flex gap-1">
                <button onClick={() => fetchSubtypes(selectedType.id)} className="p-1 hover:bg-gray-200 rounded">
                  <RefreshCw className="w-4 h-4 text-gray-600" />
                </button>
                <button onClick={() => { setEditingSubtype(null); setShowSubtypeModal(true); }}
                  className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
            )}
          </div>
          {!selectedType ? (
            <div className="p-6 text-center text-sm text-gray-400">← Select a service type</div>
          ) : subtypesLoading ? (
            <div className="p-4"><LoadingSpinner /></div>
          ) : (
            <ul className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
              {subtypes.length === 0 ? (
                <li className="px-4 py-6 text-center text-sm text-gray-500">No subtypes. Click Add to create one.</li>
              ) : subtypes.map(s => (
                <li key={s.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    {s.icon ? (
                      <img src={s.icon} alt="" className="w-8 h-8 object-contain rounded border"
                        onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-8 h-8 rounded border bg-gray-100 flex items-center justify-center text-gray-300 text-xs">
                        img
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-sm text-gray-900">{s.subtype_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{s.subtype_code}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {s.is_active ? 'Active' : 'Off'}
                    </span>
                    <button onClick={() => { setEditingSubtype(s); setShowSubtypeModal(true); }}
                      className="p-1 text-blue-600 hover:text-blue-800">
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setDeleteSubtypeId(s.id)}
                      className="p-1 text-red-600 hover:text-red-800">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selectedType && (
        <TransitionsPanel type={selectedType} statuses={statuses} onSaved={handleTransitionsSaved} />
      )}

      <TypeModal isOpen={showTypeModal} onClose={() => setShowTypeModal(false)}
        onSave={handleSaveType} type={editingType} schemes={schemes} />
      <SubtypeModal isOpen={showSubtypeModal} onClose={() => setShowSubtypeModal(false)}
        onSave={handleSaveSubtype} subtype={editingSubtype} serviceTypeId={selectedType?.id} />
      <ConfirmDialog isOpen={!!deleteTypeId} onClose={() => setDeleteTypeId(null)} onConfirm={confirmDeleteType}
        title="Delete Service Type" message="Delete this type and all its subtypes?" />
      <ConfirmDialog isOpen={!!deleteSubtypeId} onClose={() => setDeleteSubtypeId(null)} onConfirm={confirmDeleteSubtype}
        title="Delete Subtype" message="Delete this subtype?" />
    </div>
  );
};

export default SchemeServiceSetup;
